import { useEffect, useState } from "react";
import { Check, AlertTriangle, Download, Send, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, getToken, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DOC_LABEL = { en_attente: "En attente", validee: "Validée", a_refaire: "À refaire" };
const DOC_CLS = { en_attente: "bg-amber-100 text-amber-700", validee: "bg-green-100 text-green-700", a_refaire: "bg-red-100 text-red-700" };

export default function VisaFilesAdmin() {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/visa/files").then(({ data }) => {
    setFiles(data);
    if (selected) setSelected(data.find((f) => f.id === selected.id) || null);
  }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setStep = async (idx) => {
    await api.patch(`/admin/visa/files/${selected.id}/step`, { step_index: Number(idx) }).catch(() => {});
    toast.success("Étape mise à jour");
    load();
  };

  const setDoc = async (i, status) => {
    let comment = "";
    if (status === "a_refaire") {
      comment = window.prompt("Commentaire pour le client (raison du refus) :") || "";
    }
    await api.patch(`/admin/visa/files/${selected.id}/documents/${i}`, { status, comment }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    toast.success("Pièce mise à jour");
    load();
  };

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    await api.post(`/visa/files/${selected.id}/messages`, { text: msg }).catch(() => {});
    setMsg("");
    load();
  };

  if (selected) {
    return (
      <div>
        <button onClick={() => setSelected(null)} className="text-sm text-muted-foreground mb-4 hover:text-primary">← Retour à la liste</button>
        <h2 className="font-head text-2xl font-extrabold text-primary mb-1">{selected.pathway_name}</h2>
        <p className="text-muted-foreground text-sm mb-5">{selected.user_name} · {selected.user_email}</p>

        <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-5 mb-5">
          <label className="text-sm font-semibold text-primary">Étape actuelle</label>
          <select value={selected.current_step_index} onChange={(e) => setStep(e.target.value)} className="w-full h-11 rounded-xl border border-input px-3 bg-white mt-1" data-testid="admin-step-select">
            {selected.steps.map((s, i) => <option key={i} value={i}>{i + 1}. {s}</option>)}
          </select>
        </div>

        <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-5 mb-5">
          <h3 className="font-head font-bold text-primary mb-3">Pièces</h3>
          <div className="space-y-3">
            {selected.documents.map((d, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-3" data-testid={`admin-doc-${i}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-2 text-sm font-semibold text-primary"><FileText className="h-4 w-4" /> {d.name}</span>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${DOC_CLS[d.status]}`}>{DOC_LABEL[d.status]}</span>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {d.file_id ? (
                    <a href={`${API}/files/${d.file_id}/download?auth=${getToken()}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-accent font-semibold" data-testid={`admin-doc-view-${i}`}>
                      <Download className="h-3.5 w-3.5" /> {d.original_filename || "Fichier"}
                    </a>
                  ) : <span className="text-xs text-muted-foreground">Aucun fichier envoyé</span>}
                </div>
                {d.comment && <p className="text-xs text-red-600 mt-1">Note : {d.comment}</p>}
                <div className="flex gap-1 mt-2">
                  <Button size="sm" onClick={() => setDoc(i, "validee")} className="rounded-full bg-green-600 hover:bg-green-700 text-white h-8" data-testid={`doc-validate-${i}`}><Check className="h-3.5 w-3.5 mr-1" /> Valider</Button>
                  <Button size="sm" variant="outline" onClick={() => setDoc(i, "a_refaire")} className="rounded-full h-8 text-red-600 border-red-200" data-testid={`doc-reject-${i}`}><AlertTriangle className="h-3.5 w-3.5 mr-1" /> À refaire</Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-5">
          <h3 className="font-head font-bold text-primary mb-3">Messagerie</h3>
          <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
            {selected.messages.length === 0 && <p className="text-sm text-muted-foreground">Aucun message.</p>}
            {selected.messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.sender_role === "admin" ? "bg-accent text-accent-foreground" : "bg-secondary text-primary"}`}>
                  <div className="text-[11px] opacity-70">{m.sender_role === "admin" ? "Équipe" : m.sender_name}</div>
                  {m.text}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="flex gap-2">
            <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Répondre au client..." className="rounded-full" data-testid="admin-message-input" />
            <Button type="submit" className="rounded-full bg-accent text-accent-foreground px-4" data-testid="admin-send-message-btn"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-head text-2xl font-extrabold text-primary mb-4">Dossiers visa</h2>
      <div className="space-y-3">
        {files.length === 0 && <p className="text-muted-foreground text-sm">Aucun dossier.</p>}
        {files.map((f) => (
          <button key={f.id} onClick={() => setSelected(f)} className="w-full text-left bg-white rounded-2xl soft-shadow border border-gray-100 p-4 flex items-center justify-between card-hover" data-testid={`admin-visa-${f.id}`}>
            <div>
              <h3 className="font-semibold text-primary">{f.pathway_name}</h3>
              <p className="text-sm text-muted-foreground">{f.user_name} · Étape : {f.steps[f.current_step_index]}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-accent" />
          </button>
        ))}
      </div>
    </div>
  );
}
